import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import RefreshIcon from "@mui/icons-material/Refresh"
import { Box, Button, Collapse, Typography } from "@mui/material"
import React, { useState } from "react"
import { isRouteErrorResponse, useNavigate, useRouteError } from "react-router-dom"

// The colours of the charter of Time To Beem: navy for the structure, green for the signature
const NAVY = "#070F27"
const GREEN = "#0BE2A0"
const GREEN_DARK = "#08CA8F"
const GREEN_LIGHT = "#E5FFF4"
const TEXT_2 = "rgba(7,15,39,0.65)"

/** What the error says, for the technical details. */
function messageOf(error: unknown): string {
  if (isRouteErrorResponse(error)) return `${error.status} ${error.statusText}`
  if (error instanceof Error) return `${error.name}: ${error.message}`
  return String(error)
}

/**
 * A page of the application that could not be loaded after an update (a module renamed, a new version deployed): it
 * comes back by reloading.
 */
export function isStaleModuleError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error)
  return /dynamically imported module|does not provide an export|Importing a module script failed|error loading dynamically/i.test(
    message
  )
}

/** A building whose top block slipped, a wrench beside it: drawn in navy and green, as the illustrations of TTB. */
function ErrorIllustration(): React.JSX.Element {
  return (
    <svg width="220" height="170" viewBox="0 0 220 170" fill="none" aria-hidden="true">
      <defs>
        <radialGradient id="ttb-error-halo" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor={GREEN} stopOpacity="0.28" />
          <stop offset="100%" stopColor={GREEN} stopOpacity="0" />
        </radialGradient>
      </defs>
      <ellipse cx="110" cy="92" rx="104" ry="74" fill="url(#ttb-error-halo)" />
      {/* the ground */}
      <path d="M28 146h164" stroke={NAVY} strokeOpacity="0.16" strokeWidth="3" strokeLinecap="round" />
      {/* the building, its floors */}
      <rect x="62" y="66" width="76" height="80" rx="6" fill="#FFFFFF" stroke={NAVY} strokeWidth="3" />
      <path d="M62 93h76M62 120h76" stroke={NAVY} strokeOpacity="0.25" strokeWidth="2" />
      {[74, 94, 114].map((x) =>
        [74, 101].map((y) => <rect key={`${x}-${y}`} x={x} y={y} width="12" height="12" rx="2" fill={GREEN_LIGHT} />)
      )}
      <rect x="90" y="126" width="20" height="20" rx="2" fill={GREEN} />
      {/* the top block, slipped off */}
      <g transform="rotate(-14 118 48)">
        <rect x="80" y="32" width="64" height="26" rx="5" fill={GREEN} stroke={NAVY} strokeWidth="3" />
        <path d="M94 45h36" stroke={NAVY} strokeWidth="3" strokeLinecap="round" />
      </g>
      {/* little jolts */}
      <path d="M150 30l8-6M154 42l10-1M60 40l-9-5" stroke={GREEN_DARK} strokeWidth="3" strokeLinecap="round" />
      {/* the wrench, to put it back */}
      <g transform="rotate(35 172 118)">
        <rect x="167" y="104" width="10" height="38" rx="5" fill={NAVY} />
        <path d="M162 100a12 12 0 1 1 20 0l-4 6h-12z" fill={NAVY} />
        <circle cx="172" cy="96" r="4" fill="#FFFFFF" />
      </g>
    </svg>
  )
}

/**
 * The page shown instead of a page of the application that failed: what happened in plain words, the way back to the
 * page before (or a reload), and the technical details for the support, folded.
 */
export function AppErrorPage(): React.JSX.Element {
  const error = useRouteError()
  const navigate = useNavigate()
  const [detailsShown, setDetailsShown] = useState(false)
  const stale = isStaleModuleError(error)
  // the page before in the application, if there is one; else the home page
  const canGoBack = window.history.length > 1
  const goBack = (): void => {
    if (canGoBack) navigate(-1)
    else navigate("/")
  }

  return (
    <Box
      data-testid="app-error-page"
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#F4F7FB",
        p: 2,
      }}>
      <Box
        sx={{
          width: "100%",
          maxWidth: 560,
          textAlign: "center",
          backgroundColor: "#FFFFFF",
          border: "1px solid rgba(7,15,39,0.10)",
          borderRadius: "18px",
          boxShadow: "0 1px 2px rgba(7,15,39,0.04), 0 10px 28px -12px rgba(7,15,39,0.16)",
          px: { xs: 3, sm: 5 },
          py: 4,
        }}>
        <ErrorIllustration />
        <Typography
          component="h1"
          sx={{ mt: 1, fontSize: 26, fontWeight: 600, lineHeight: 1.2, letterSpacing: "-0.02em", color: NAVY }}>
          {stale ? (
            <>
              Une{" "}
              <Box component="span" sx={{ color: GREEN_DARK }}>
                nouvelle version
              </Box>{" "}
              est disponible
            </>
          ) : (
            <>
              Nous n&apos;avons pas pu{" "}
              <Box component="span" sx={{ color: GREEN_DARK }}>
                charger cette page
              </Box>
            </>
          )}
        </Typography>
        <Typography sx={{ mt: 1.5, fontSize: 15, lineHeight: 1.6, color: TEXT_2 }}>
          {stale
            ? "L'application a été mise à jour pendant que vous travailliez. Rechargez la page pour continuer : vos données enregistrées sont conservées."
            : "Une erreur inattendue l'a empêchée de s'afficher. Vos données enregistrées ne sont pas perdues : revenez à la page précédente ou rechargez celle-ci."}
        </Typography>

        <Box display="flex" gap={1.5} justifyContent="center" flexWrap="wrap" sx={{ mt: 3 }}>
          <Button
            onClick={stale ? () => window.location.reload() : goBack}
            startIcon={stale ? <RefreshIcon /> : <ArrowBackIcon />}
            data-testid="app-error-primary"
            sx={{
              px: 3,
              py: 1.25,
              borderRadius: "11px",
              textTransform: "none",
              fontWeight: 600,
              fontSize: 15,
              color: NAVY,
              backgroundColor: GREEN,
              boxShadow: "0 6px 18px -2px rgba(11,226,160,0.45)",
              ":hover": { backgroundColor: GREEN_DARK },
            }}>
            {stale ? "Recharger la page" : "Revenir en arrière"}
          </Button>
          <Button
            onClick={stale ? goBack : () => window.location.reload()}
            startIcon={stale ? <ArrowBackIcon /> : <RefreshIcon />}
            data-testid="app-error-secondary"
            sx={{
              px: 3,
              py: 1.25,
              borderRadius: "11px",
              textTransform: "none",
              fontWeight: 600,
              fontSize: 15,
              color: NAVY,
              border: "1px solid rgba(7,15,39,0.16)",
              ":hover": { backgroundColor: "#EEF2F8" },
            }}>
            {stale ? "Revenir en arrière" : "Recharger la page"}
          </Button>
        </Box>

        <Button
          size="small"
          onClick={() => setDetailsShown((shown) => !shown)}
          endIcon={
            <ExpandMoreIcon sx={{ transform: detailsShown ? "rotate(180deg)" : "none", transition: "transform 0.2s" }} />
          }
          sx={{ mt: 2.5, textTransform: "none", color: "rgba(7,15,39,0.45)", fontSize: 12.5 }}>
          Détails techniques pour le support
        </Button>
        <Collapse in={detailsShown}>
          <Box
            component="pre"
            data-testid="app-error-details"
            sx={{
              mt: 1,
              p: 1.5,
              textAlign: "left",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              fontFamily: "'JetBrains Mono', Consolas, Monaco, monospace",
              fontSize: 11.5,
              color: TEXT_2,
              backgroundColor: "#EEF2F8",
              borderRadius: "9px",
              maxHeight: 180,
              overflow: "auto",
            }}>
            {messageOf(error)}
          </Box>
        </Collapse>
      </Box>
    </Box>
  )
}
